import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import NavigationBar from './NavigationBar';
import HamburgerMenu from './HamburgerMenu';
import Footer from './Footer';

function FounderPage({ navigateTo, goBack, isLoggedIn, userProfile }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showInfoPackModal, setShowInfoPackModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [isGrantingFounder, setIsGrantingFounder] = useState(false);
  const [paypalLoaded, setPaypalLoaded] = useState(false);
  
  // Real founder status from database
  const isFreeFounder = userProfile?.is_free_founder || false;
  const isPaidFounder = userProfile?.is_paid_founder || false;
  const userEmail = userProfile?.email || '';

  // Load PayPal SDK
  useEffect(() => {
    if (typeof window.paypal !== 'undefined') {
      setPaypalLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://www.paypal.com/sdk/js?client-id=AeaHatj51R_DftEaShCW24ZeGAJyeDSITUOIXWoKQdV0YsDybmDVGg74PYbB7sFGIV6SmWUHmJay33Ku&currency=GBP';
    script.async = true;
    script.onload = () => setPaypalLoaded(true);
    script.onerror = () => console.error('PayPal SDK failed to load');
    document.body.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  // Render PayPal button when modal opens
  useEffect(() => {
    if (showPaymentModal && paypalLoaded && window.paypal) {
      try {
        // Clear any existing buttons first
        const container = document.getElementById('paypal-button-container');
        if (container) {
          container.innerHTML = '';
        }
        
        // Render the PayPal button using standard Buttons API
        window.paypal.Buttons({
          // Use your hosted button ID
          fundingSource: window.paypal.FUNDING.PAYPAL,
          
          createOrder: function(data, actions) {
            // This creates a PayPal order
            return actions.order.create({
              purchase_units: [{
                description: 'SudokuMatch Paid Founder Status',
                amount: {
                  currency_code: 'GBP',
                  value: '20.00'
                }
              }]
            });
          },
          
          onApprove: async function(data, actions) {
            // Capture the payment
            return actions.order.capture().then(async function(details) {
              // Payment successful - grant founder status
              if (!isLoggedIn || !userProfile) {
                alert('Please log in to complete your purchase');
                return;
              }

              setIsGrantingFounder(true);
              
              try {
                const { error } = await supabase
                  .from('profiles')
                  .update({ is_paid_founder: true })
                  .eq('id', userProfile.id);

                if (error) {
                  console.error('Error granting founder status:', error);
                  alert('Payment successful but failed to grant Founder status. Please contact support.');
                } else {
                  // Send welcome email
                  try {
                    const { data: { session } } = await supabase.auth.getSession();
                    if (session) {
                      await supabase.functions.invoke('send-founder-welcome', {
                        body: { userId: userProfile.id }
                      });
                    }
                  } catch (emailError) {
                    console.error('Error sending welcome email:', emailError);
                    // Don't block the flow if email fails
                  }
                  
                  setShowPaymentModal(false);
                  alert('Payment successful! 🎉\n\nYour Paid Founder status is now active. Check your email for a welcome message!\n\nThank you for your support!');
                  navigateTo('account');
                }
              } catch (error) {
                console.error('Error granting founder status:', error);
                alert('Payment successful but an error occurred. Please contact support.');
              } finally {
                setIsGrantingFounder(false);
              }
            });
          },
          
          onError: function(err) {
            console.error('PayPal error:', err);
            alert('An error occurred with PayPal. Please try again.');
          }
        }).render('#paypal-button-container');
      } catch (error) {
        console.error('Error rendering PayPal button:', error);
      }
    }
  }, [showPaymentModal, paypalLoaded, isLoggedIn, userProfile]);

  // Detect mobile/desktop
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Grant Free Founder status
  const handleClaimFreeFounder = async () => {
    if (!isLoggedIn || !userProfile) {
      alert('Please log in to claim Founder status');
      return;
    }

    if (isFreeFounder) {
      alert('You are already a Free Founder!');
      return;
    }

    setIsGrantingFounder(true);
    
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ is_free_founder: true })
        .eq('id', userProfile.id);

      if (error) {
        console.error('Error granting founder status:', error);
        alert('Failed to grant Founder status. Please try again.');
      } else {
        alert('Welcome to the Founder Programme! 🎉\n\nYour Free Founder status is now active.');
        // Navigate to account page
        navigateTo('account');
      }
    } catch (error) {
      console.error('Error granting founder status:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setIsGrantingFounder(false);
    }
  };

  const handlePaidFounderClick = () => {
    if (isPaidFounder) {
      alert('You\'re already a Founding Member!');
    } else if (!isLoggedIn) {
      alert('Please log in to upgrade to Founding Member');
    } else {
      setShowPaymentModal(true);
    }
  };

  const handlePayPalClick = async () => {
    // TODO: Replace with real PayPal integration
    // For V1, this will trigger the payment flow
    
    // Simulate payment success for testing
    const confirmPayment = window.confirm(
      'This will process a £20 payment via PayPal.\n\n' +
      'Click OK to proceed to PayPal checkout.\n' +
      '(Payment integration will be connected before launch)'
    );
    
    if (!confirmPayment) return;

    // For V1 testing: Mark as founding member immediately
    // In production: This happens after PayPal webhook confirms payment
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ 
          is_paid_founder: true,
          is_free_founder: true  // Paid founders also get free founder benefits
        })
        .eq('id', userProfile.id);

      if (error) {
        console.error('Error upgrading to founding member:', error);
        alert('Payment processing failed. Please try again.');
      } else {
        alert('🎉 Welcome to Founding Member!\n\nYour upgrade is now active.');
        setShowPaymentModal(false);
        // Navigate to account instead of reloading
        navigateTo('account');
      }
    } catch (error) {
      console.error('Error upgrading to founding member:', error);
      alert('An error occurred. Please try again.');
    }
  };

  const handleNotifyMe = () => {
    // This function is no longer needed for V1 but keeping for backwards compatibility
    alert('Payment is live! Click "Pay with PayPal" to upgrade now.');
  };

  const FounderBadge = ({ type }) => {
    const isFilled = type === 'paid';
    const color = type === 'paid' ? '#B58B2E' : '#2563EB';
    
    return (
      <svg width="20" height="22" viewBox="0 0 16 18" style={{ display: 'inline-block', marginLeft: '8px', verticalAlign: 'middle' }}>
        <path
          d="M8 0 L12 4 L16 4 L16 11 L8 18 L0 11 L0 4 L4 4 Z"
          fill={isFilled ? color : 'none'}
          stroke={color}
          strokeWidth="1.5"
        />
      </svg>
    );
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
      padding: isMobile ? '20px 16px 60px' : '40px 20px 80px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      {!isMobile && <NavigationBar currentPage="founder" isLoggedIn={isLoggedIn} navigateTo={navigateTo} userProfile={userProfile} />}
      {isMobile && <HamburgerMenu currentPage="founder" isLoggedIn={isLoggedIn} navigateTo={navigateTo} userProfile={userProfile} />}
      
      {/* Payment Modal */}
      {showPaymentModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }} onClick={() => setShowPaymentModal(false)}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '16px',
              padding: isMobile ? '32px 24px' : '40px 32px',
              maxWidth: '500px',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.4)'
            }}
          >
            <h2 style={{
              fontSize: '24px',
              fontWeight: '800',
              color: '#1F2937',
              margin: '0 0 8px 0'
            }}>
              Become a Founding Member
            </h2>
            <p style={{
              fontSize: '14px',
              color: '#6B7280',
              margin: '0 0 24px 0'
            }}>
              One-time payment · No subscriptions
            </p>

            {/* Price */}
            <div style={{
              background: '#F9FAFB',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '24px',
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: '42px',
                fontWeight: '800',
                color: '#1F2937',
                marginBottom: '8px'
              }}>
                £20
              </div>
              <div style={{
                fontSize: '14px',
                color: '#6B7280',
                fontWeight: '500'
              }}>
                One-time · Lifetime access
              </div>
            </div>

            {/* Benefits Reminder */}
            <div style={{
              marginBottom: '24px'
            }}>
              <div style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#1F2937',
                marginBottom: '12px'
              }}>
                Included:
              </div>
              <ul style={{
                margin: '0',
                padding: '0 0 0 20px',
                fontSize: '14px',
                color: '#4B5563',
                lineHeight: '1.8'
              }}>
                <li>Distinct Founding Member badge</li>
                <li>No ads for life</li>
                <li>Name on the Founder Wall</li>
                <li>Priority feedback channels and access to new features</li>
                <li>All Free Founder benefits</li>
              </ul>
            </div>

            {/* Payment Options */}
            <div style={{
              background: '#EFF6FF',
              border: '1px solid #DBEAFE',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '24px',
              fontSize: '13px',
              color: '#1E40AF',
              lineHeight: '1.5'
            }}>
              <strong>Secure payment via PayPal</strong><br />
              You'll be redirected to PayPal to complete your payment safely.
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '12px',
              flexDirection: 'column'
            }}>
              {/* PayPal Button Container */}
              <div id="paypal-button-container" style={{
                minHeight: '50px'
              }}></div>
              
              <button
                onClick={() => setShowPaymentModal(false)}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: '#F3F4F6',
                  color: '#4B5563',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Information Pack Modal - Full PDF for Founders */}
      {showInfoPackModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }} onClick={() => setShowInfoPackModal(false)}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '16px',
              padding: '20px',
              maxWidth: '800px',
              width: '100%',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 60px rgba(0,0,0,0.4)'
            }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px'
            }}>
              <h2 style={{
                fontSize: '20px',
                fontWeight: '800',
                color: '#1F2937',
                margin: '0'
              }}>
                Founder Information Pack
              </h2>
              <button
                onClick={() => setShowInfoPackModal(false)}
                style={{
                  background: '#F3F4F6',
                  border: 'none',
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ✕
              </button>
            </div>
            <iframe
              src="/SudokuMatch_Founder_Information_Pack.pdf"
              style={{
                width: '100%',
                height: '70vh',
                border: 'none',
                borderRadius: '8px'
              }}
              title="Founder Information Pack"
            />
          </div>
        </div>
      )}

      {/* Preview Modal - Preview PDF for Guests */}
      {showPreviewModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }} onClick={() => setShowPreviewModal(false)}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '600px',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
              textAlign: 'center'
            }}
          >
            <h2 style={{
              fontSize: '20px',
              fontWeight: '800',
              color: '#1F2937',
              margin: '0 0 20px 0'
            }}>
              Founder Programme Preview
            </h2>
            <iframe
              src="/SudokuMatch_Founder_Preview.pdf"
              style={{
                width: '100%',
                height: '500px',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                marginBottom: '20px'
              }}
              title="Founder Preview"
            />
            <button
              onClick={() => setShowPreviewModal(false)}
              style={{
                padding: '12px 32px',
                background: '#FF8C00',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                fontSize: '15px',
                fontWeight: '700',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(255, 140, 0, 0.4)'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      <div style={{
        maxWidth: '1000px',
        margin: '0 auto'
      }}>
        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '40px',
          color: 'white'
        }}>
          <h1 style={{
            fontSize: isMobile ? '32px' : '42px',
            fontWeight: '800',
            margin: '0 0 12px 0',
            letterSpacing: '-0.5px'
          }}>
            The Founder Programme
          </h1>
          <p style={{
            fontSize: '16px',
            opacity: 0.9,
            margin: '0',
            fontWeight: '500'
          }}>
            Early supporters helping shape the future of competitive Sudoku.
          </p>
        </div>

        {/* Why Founders Exist */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: isMobile ? '24px' : '32px',
          marginBottom: '32px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
        }}>
          <p style={{
            fontSize: '15px',
            color: '#4B5563',
            lineHeight: '1.7',
            margin: '0'
          }}>
            SudokuMatch is being built carefully and independently. The Founder programme exists for players who want to support its development and help shape what comes next. Founders help guide the product through feedback while enjoying early access and recognition as the platform grows. Limited-time signup. Lifetime perks.
          </p>
        </div>

        {/* Founder Tiers */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: '20px',
          marginBottom: '40px'
        }}>
          {/* Free Founder Card */}
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: isMobile ? '28px 24px' : '32px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
            border: isFreeFounder ? '2px solid #2563EB' : 'none'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '16px'
            }}>
              <h2 style={{
                fontSize: '24px',
                fontWeight: '800',
                color: '#1F2937',
                margin: '0'
              }}>
                Free Founder
              </h2>
              <FounderBadge type="free" />
            </div>

            <p style={{
              fontSize: '14px',
              color: '#6B7280',
              margin: '0 0 24px 0',
              lineHeight: '1.5'
            }}>
              Automatically available to early users who join SudokuMatch during its early development.
            </p>

            <div style={{
              marginBottom: '24px'
            }}>
              <div style={{
                fontSize: '13px',
                fontWeight: '700',
                color: '#6B7280',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '12px'
              }}>
                Included Perks
              </div>
              <ul style={{
                margin: '0',
                padding: '0 0 0 20px',
                fontSize: '14px',
                color: '#4B5563',
                lineHeight: '1.8'
              }}>
                <li>Founder badge on profile and leaderboard</li>
                <li>Early access to new features</li>
                <li>Access to Founder community and feedback channels</li>
                <li>Access to the full Founder Information Pack</li>
                <li>Recognition as an early supporter</li>
              </ul>
            </div>

            {isLoggedIn ? (
              isFreeFounder ? (
                <div style={{
                  padding: '12px',
                  background: '#EFF6FF',
                  border: '2px solid #2563EB',
                  borderRadius: '10px',
                  fontSize: '15px',
                  fontWeight: '700',
                  color: '#2563EB',
                  textAlign: 'center'
                }}>
                  ✓ You're Already a Free Founder
                </div>
              ) : (
                <button
                  onClick={handleClaimFreeFounder}
                  disabled={isGrantingFounder}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: isGrantingFounder ? '#9CA3AF' : '#2563EB',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '15px',
                    fontWeight: '700',
                    cursor: isGrantingFounder ? 'not-allowed' : 'pointer',
                    boxShadow: '0 4px 12px rgba(37, 99, 235, 0.4)'
                  }}
                >
                  {isGrantingFounder ? 'Claiming...' : 'Claim Free Founder Status'}
                </button>
              )
            ) : (
              <button
                onClick={() => navigateTo('login')}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: '#2563EB',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '15px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(37, 99, 235, 0.4)'
                }}
              >
                Create Account to Join
              </button>
            )}
          </div>

          {/* Founding Member Card */}
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: isMobile ? '28px 24px' : '32px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
            border: isPaidFounder ? '2px solid #B58B2E' : 'none'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '16px'
            }}>
              <h2 style={{
                fontSize: '24px',
                fontWeight: '800',
                color: '#1F2937',
                margin: '0'
              }}>
                Founding Member
              </h2>
              <FounderBadge type="paid" />
            </div>

            <p style={{
              fontSize: '14px',
              color: '#6B7280',
              margin: '0 0 24px 0',
              lineHeight: '1.5'
            }}>
              A one-time upgrade for players who want to go a step further in supporting SudokuMatch.
            </p>

            <div style={{
              marginBottom: '24px'
            }}>
              <div style={{
                fontSize: '13px',
                fontWeight: '700',
                color: '#6B7280',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '12px'
              }}>
                Included Perks
              </div>
              <ul style={{
                margin: '0',
                padding: '0 0 0 20px',
                fontSize: '14px',
                color: '#4B5563',
                lineHeight: '1.8'
              }}>
                <li>Distinct Founding Member badge</li>
                <li>No ads for life</li>
                <li>Name on the Founder Wall</li>
                <li>Priority feedback and access to new competitive features</li>
                <li>All Free Founder benefits included</li>
              </ul>
            </div>

            {isPaidFounder ? (
              <div style={{
                padding: '12px',
                background: '#FEF3C7',
                border: '2px solid #B58B2E',
                borderRadius: '10px',
                fontSize: '15px',
                fontWeight: '700',
                color: '#92400E',
                textAlign: 'center'
              }}>
                ✓ You're a Founding Member
              </div>
            ) : (
              <>
                <button
                  onClick={handlePaidFounderClick}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: '#FF8C00',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '15px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(255, 140, 0, 0.4)',
                    marginBottom: '8px'
                  }}
                >
                  Become a Founding Member
                </button>
                <div style={{
                  fontSize: '13px',
                  color: '#9CA3AF',
                  textAlign: 'center'
                }}>
                  One-time payment · No subscriptions
                </div>
              </>
            )}
          </div>
        </div>

        {/* Information Pack */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: '16px',
          padding: isMobile ? '24px' : '28px',
          marginBottom: '40px',
          textAlign: 'center',
          color: 'white'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '700',
            margin: '0 0 12px 0'
          }}>
            Founder Information Pack
          </h3>
          <p style={{
            fontSize: '14px',
            opacity: 0.9,
            margin: '0 0 20px 0',
            lineHeight: '1.6'
          }}>
            A detailed overview of how SudokuMatch plans to grow, what's coming next, and how Founders can be involved.
          </p>
          <button
            onClick={() => {
              if (isFreeFounder || isPaidFounder) {
                setShowInfoPackModal(true);
              } else {
                setShowPreviewModal(true);
              }
            }}
            style={{
              padding: '12px 28px',
              background: 'rgba(255, 255, 255, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              color: 'white',
              borderRadius: '10px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.2)';
            }}
          >
            {isFreeFounder || isPaidFounder ? 'View Information Pack' : 'View Preview'}
          </button>
        </div>

        {/* What This Is / Isn't */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: isMobile ? '28px 24px' : '32px',
          marginBottom: '32px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
        }}>
          <h3 style={{
            fontSize: '20px',
            fontWeight: '800',
            color: '#1F2937',
            margin: '0 0 24px 0'
          }}>
            What Being a Founder Means
          </h3>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: isMobile ? '24px' : '32px'
          }}>
            <div>
              <div style={{
                fontSize: '14px',
                fontWeight: '700',
                color: '#059669',
                marginBottom: '12px'
              }}>
                ✓ What it means
              </div>
              <ul style={{
                margin: '0',
                padding: '0 0 0 20px',
                fontSize: '14px',
                color: '#4B5563',
                lineHeight: '1.8'
              }}>
                <li>Supporting development</li>
                <li>Helping shape the product</li>
                <li>Early access and recognition</li>
                <li>Being part of the journey</li>
              </ul>
            </div>

            <div>
              <div style={{
                fontSize: '14px',
                fontWeight: '700',
                color: '#DC2626',
                marginBottom: '12px'
              }}>
                ✗ What it does NOT mean
              </div>
              <ul style={{
                margin: '0',
                padding: '0 0 0 20px',
                fontSize: '14px',
                color: '#4B5563',
                lineHeight: '1.8'
              }}>
                <li>Competitive advantage</li>
                <li>Pay-to-win mechanics</li>
                <li>Financial stake or ownership</li>
                <li>Special leaderboard treatment</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Founder Wall Placeholder */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: isMobile ? '40px 24px' : '60px 32px',
          textAlign: 'center',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
        }}>
          <div style={{
            fontSize: '48px',
            marginBottom: '16px'
          }}>
            🏆
          </div>
          <h3 style={{
            fontSize: '24px',
            fontWeight: '800',
            color: '#1F2937',
            margin: '0 0 12px 0'
          }}>
            Founder Wall
          </h3>
          <p style={{
            fontSize: '15px',
            color: '#6B7280',
            margin: '0'
          }}>
            Founding Members will be recognised here.
          </p>
        </div>
      </div>

      <div style={{ marginTop: '40px' }}>
        <Footer navigateTo={navigateTo} />
      </div>
    </div>
  );
}

export default FounderPage;
